import type {
  CountryOption,
  StateOption,
} from 'app/profile/renter/refactor_rental_history/components/RentalHistoryForm/types'
import { countryListAlpha3 } from 'app/utils/country/helpers'

// Country code constants
export const COUNTRY_CODE_AUSTRALIA = 'AUS'

/**
 * Helper function to safely get country code from form values
 */
export const getCountryCode = (country: any): string => {
  if (!country) return ''

  // If it's already a string (country code), return it
  if (typeof country === 'string') return country

  // If it's an option object, extract the value
  if (country?.option?.value) return country.option.value
  if (country?.optionMeta?.value) return country.optionMeta.value
  if (country?.value) return country.value

  return ''
}

/**
 * Helper function to check if address is Australian, handling different country formats
 */
export const isAddressAustralian = (address: any): boolean => {
  const countryCode = getCountryCode(address?.country)
  return (
    countryCode === COUNTRY_CODE_AUSTRALIA ||
    countryCode?.toUpperCase() === 'AUSTRALIA'
  )
}

/**
 * Helper function to safely extract state display value from StateOption or string
 * Fixes "[object Object]" display issue when switching from SelectField to InputField
 */
export const getStateDisplayValue = (state: string | StateOption): string => {
  if (!state) return ''

  // If it's already a string, return it
  if (typeof state === 'string') return state

  // If it's a StateOption object, extract the display value
  if (state?.option?.value) return state.option.value
  if (state?.optionMeta?.value) return state.optionMeta.value
  if (state?.optionMeta?.displayText) return state.optionMeta.displayText

  return ''
}

/**
 * State options for Australian addresses
 */
export const australianStates: StateOption[] = [
  {
    option: { value: 'NSW', label: 'NSW' },
    optionMeta: { id: 'NSW', displayText: 'NSW', value: 'NSW' },
  },
  {
    option: { value: 'VIC', label: 'VIC' },
    optionMeta: { id: 'VIC', displayText: 'VIC', value: 'VIC' },
  },
  {
    option: { value: 'QLD', label: 'QLD' },
    optionMeta: { id: 'QLD', displayText: 'QLD', value: 'QLD' },
  },
  {
    option: { value: 'WA', label: 'WA' },
    optionMeta: { id: 'WA', displayText: 'WA', value: 'WA' },
  },
  {
    option: { value: 'SA', label: 'SA' },
    optionMeta: { id: 'SA', displayText: 'SA', value: 'SA' },
  },
  {
    option: { value: 'TAS', label: 'TAS' },
    optionMeta: { id: 'TAS', displayText: 'TAS', value: 'TAS' },
  },
  {
    option: { value: 'ACT', label: 'ACT' },
    optionMeta: { id: 'ACT', displayText: 'ACT', value: 'ACT' },
  },
  {
    option: { value: 'NT', label: 'NT' },
    optionMeta: { id: 'NT', displayText: 'NT', value: 'NT' },
  },
]

/**
 * Helper function to create country options from country helpers
 */
export const createCountryOptions = (): CountryOption[] => {
  // Get all countries sorted alphabetically by name
  const allCountries = Object.keys(countryListAlpha3).sort((a, b) =>
    countryListAlpha3[a as keyof typeof countryListAlpha3].localeCompare(
      countryListAlpha3[b as keyof typeof countryListAlpha3],
    ),
  )

  const countryOptions = allCountries.map((countryCode) => ({
    option: {
      value: countryCode,
      label: countryListAlpha3[countryCode as keyof typeof countryListAlpha3],
    },
    optionMeta: {
      id: countryCode,
      displayText:
        countryListAlpha3[countryCode as keyof typeof countryListAlpha3],
      value: countryCode,
    },
  }))

  return countryOptions
}

/**
 * Country options using the helper function
 */
export const countryOptions = createCountryOptions()
